import { ISharedInvoicePaymentReq } from '@monetize/types/app';
import { loadStripe } from '@stripe/stripe-js';
import { FunctionComponent as FC, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAtom } from 'jotai';
import { handleApiErrorToast } from '../../api/axios';
import { useInvoicePaymentForInvoiceShare } from '../../api/shareService';
import AppLoading from '../../components/App/AppLoading';
import { ROUTES } from '../../constants';
import { useQuery } from '../../hooks';
import { tempSharedInvoicePaymentState } from '../../store/global.store';
import { processClientSecretId } from '../../utils/stripe';

// this component only used if Stripe determines the payment requires
// a callback, otherwise we handle the payment response in SharedInvoiceFormFields
export const StripePaymentCallback: FC = () => {
  const params = useParams();
  const tenantId = params.tenantId || '';
  const invoiceSecretId = params.invoiceSecretId || '';
  const query = useQuery();
  const navigate = useNavigate();

  const [sharedInvoicePayment, setSharedInvoicePayment] = useAtom(
    tempSharedInvoicePaymentState,
  );
  const { mutateAsync: doCreateSharedInvoicePayment } =
    useInvoicePaymentForInvoiceShare();

  const clientSecretId = query.get('setup_intent_client_secret');

  useEffect(() => {
    const run = async () => {
      if (!sharedInvoicePayment) {
        return;
      }

      try {
        const stripe = await loadStripe(sharedInvoicePayment.stripePublicKey);

        if (!stripe) {
          throw new Error('Sorry, failed to load stripe.');
        }

        if (!clientSecretId) {
          throw new Error('Sorry, failed to connect.');
        }

        const { setupIntent, message } = await processClientSecretId(
          stripe,
          clientSecretId,
        );

        const newPayment: ISharedInvoicePaymentReq = {
          amount: sharedInvoicePayment.amount,
          paymentToken: setupIntent.id,
          paymentMethodName:
            sharedInvoicePayment.billingDetails.paymentMethodName ||
            sharedInvoicePayment.billingDetails.fullName, // the paymentMethodName property is not returned in the API response
          description: '',
          billingDetails: sharedInvoicePayment.billingDetails,
        };
        await doCreateSharedInvoicePayment({
          invoiceSecretId,
          tenantId,
          body: newPayment,
        });

        setSharedInvoicePayment(null);
      } catch (error) {
        handleApiErrorToast(error);
      }

      // Redirect to the invoice page
      navigate(ROUTES.getInvoiceShareRoute(tenantId, invoiceSecretId), {});
    };

    run();
  }, [sharedInvoicePayment, clientSecretId]);

  return <AppLoading />;
};
